import React, {FocusEventHandler} from 'react';
import Tooltip from "@mui/material/Tooltip/Tooltip";
import Button from "@mui/material/Button/Button";
import {IconButton, Theme} from '@mui/material';
import {buttonStyles} from "../../../config/Theme";
import {Denseable} from "../../../models/common/ComponentBehavioural";
import {makeStyles} from "@mui/styles";

export type ButtonVariantType =  "primary" | "secondary" | "tertiary" | "xs"

/**
 * Props for the ResponsiveButtonWithTooltip component.
 *
 * This interface defines properties for a customizable button that contains
 * optional tooltip support and responsiveness.
 *
 * @interface ResponsiveButtonWithTooltipProps
 * @extends Denseable
 *
 * @property {string} [tooltip] - Optional tooltip to be displayed when hovering over the button.
 * @property {React.ReactNode} [icon] - Optional icon element to be displayed within the button.
 * @property {string} [text] - Optional text to be displayed on the button.
 * @property {string} [className] - Optional CSS class name for additional styling.
 * @property {ButtonVariantType} [variant] - Optional variant type for the button styling.
 * @property {(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void} [onClick] - Optional callback function to be invoked when the button is clicked.
 * @property {boolean} [disabled] - Indicates whether the button is disabled.
 * @property {boolean} [active] - Indicates whether the button is currently in an active state.
 * @property {(e: FocusEventHandler<HTMLElement>) => void} [onBlur] - Optional callback function to handle blur events.
 * @property {(e: FocusEventHandler<HTMLElement>) => void} [onFocus] - Optional callback function to handle focus events.
 * @property {boolean} [nonResponsive] - Indicates whether the button should ignore responsiveness adjustments.
 * @property {React.CSSProperties} [style] - Optional inline styles to be applied to the button.
 */
export interface ResponsiveButtonWithTooltipProps extends Denseable {
    tooltip?: string
    icon?: React.ReactNode
    text?: string
    className?: string
    variant?: ButtonVariantType
    onClick?: ( e: React.MouseEvent<HTMLButtonElement, MouseEvent> ) => void
    disabled?: boolean
    active?: boolean
    onBlur?: ( e: FocusEventHandler< HTMLElement >) => void
    onFocus?: ( e: FocusEventHandler< HTMLElement >) => void
    nonResponsive?: boolean
    style?: React.CSSProperties
}

/**
 * A variable containing styles created using the `makeStyles` utility from Material-UI.
 * It defines CSS styles for various elements based on the theme and responsiveness.
 *
 * Properties:
 * - Combines button styles with additional custom styles.
 * - `sectionDesktopResponsive`: A responsive style that defaults to `display: none` and becomes a flex container with custom padding when the viewport reaches 'md' breakpoint or larger.
 * - `sectionDesktop`: A style that defines a flex container with a margin to the left.
 *
 * Note:
 * The `sectionMobile` style is commented out and not currently included in the defined styles.
 */
const useStyle = makeStyles(( theme: Theme ) => ({
    ...buttonStyles(theme),
    sectionDesktopResponsive: {
        display: 'none',
        [theme.breakpoints.up('md')]: {
            paddingRight: "10px",
            paddingLeft: "10px",
            display: "flex"
        },
    },
    sectionDesktop: {
        marginLeft: "10px",
        display: "flex"
    }
    /*sectionMobile: {
        display: "flex",
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },*/
})
);

/**
 * Renders a responsive button with an optional tooltip and various styles and behaviors based on the given props.
 *
 * @param {Object} props - The props for the component.
 * @param {Function} props.onFocus - Callback function to handle the focus event on the button.
 * @param {Function} props.onBlur - Callback function to handle the blur event on the button.
 * @param {string} [props.className] - Additional CSS class names to be applied to the button.
 * @param {Function} props.onClick - Callback function to handle the click event on the button.
 * @param {boolean} [props.disabled=false] - Indicates whether the button is disabled.
 * @param {boolean} [props.nonResponsive] - If true, the button does not adapt to responsive design.
 * @param {string} [props.tooltip] - Text to be displayed as a tooltip when hovering over the button.
 * @param {ReactNode} [props.icon] - Icon to be displayed within the button.
 * @param {boolean} [props.dense] - If true, renders a compact version of the button.
 * @param {string} [props.text] - Text label to be displayed within the button.
 * @param {Object} [props.style] - Inline style object to customize the button's appearance.
 * @param {string} [props.variant] - Variant of the button (e.g., "xs", "secondary", "tertiary").
 * @param {boolean} [props.active] - Indicates whether the button is in an active state.
 *
 * @return {JSX.Element} A responsive button component with optional tooltip, icon, and text, styled and behaving according to the provided props.
 */
function ResponsiveButtonWithTooltip ({
    onFocus, onBlur, className, onClick, disabled, nonResponsive, tooltip, icon, dense, text, style, variant, active
}: ResponsiveButtonWithTooltipProps ){
    const classes = useStyle();
    const isDisabled = disabled ?? false;
    const cssStyle = style ?  { ...style} : {  };


    if( variant === "xs" ){

        return (
            <Tooltip title={tooltip}>

                    <IconButton
                        className={ active
                            ? className ? className + ' ' + classes.tableButtonStyleActive : classes.tableButtonStyleActive
                            : className ? className + ' ' + classes.tableButtonStyle : classes.tableButtonStyle
                        }
                        onClick={onClick}
                    >
                        { icon }
                    </IconButton>
            </Tooltip>
        );
    }


    let variantClass = classes.button;

    switch( variant ) {

        case "secondary":
            variantClass = classes.buttonVariantSecondary;
            break;
        case "tertiary":
            variantClass = classes.buttonVariantTertiary;
            break;
    }

    if( active ) {

        switch( variant ) {

            case "secondary":
                variantClass = classes.buttonVariantSecondaryActive;
                break;
            case "tertiary":
                variantClass = classes.buttonVariantTertiaryActive;
                break;
            default:
                variantClass = classes.buttonActive;
        }

    }

    if( disabled ) {

        switch( variant ) {

            case "secondary":
                variantClass = classes.buttonVariantSecondaryDisabled;
                break;
            case "tertiary":
                variantClass = classes.buttonVariantTertiaryDisabled;
                break;
            default:
                variantClass = classes.disabledButton;
        }
    }




    if( dense ) {
        return (
            <Tooltip title={tooltip}>
                <span>
                    <Button

                        variant="text"
                        disabled={isDisabled}
                            style={ { ...cssStyle, textAlign: 'center'}}
                            className={ className ? className + ' ' + variantClass : variantClass}
                            onClick={ onClick}
                    >
                        { icon }
                    </Button>
                </span>
            </Tooltip>
        );
    } else {
        return (
            <Tooltip title={tooltip}>
            <span>
            <Button
                variant="text"
                disabled={isDisabled}
                    style={cssStyle}
                    className={ className ? className + ' ' + variantClass : variantClass}
                    onClick={ onClick }
            >
                { icon }
                <div className={ typeof icon === "undefined" ? "" : (  nonResponsive ? classes.sectionDesktop : classes.sectionDesktopResponsive ) }>
                    { text }
                </div>
            </Button>
            </span>
            </Tooltip>
        );
    }
}

export default ResponsiveButtonWithTooltip;